import { useCallback, useEffect, useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import type { Account } from "@/hooks/api/use-accounts";
import { useTrackedLocalStorage } from "@/hooks/use-tracked-browser-storage";

// Constants
const RECENT_ACCOUNTS_KEY = "accounts::explorer::selected-account::recent";
const SELECTED_ACCOUNTS_KEY = "accounts::explorer::selected-account::active";
const MAX_RECENT_ACCOUNTS = 10;

// Minimal type definition for a recent account with only essential fields for storage
export interface RecentAccount {
  id: string;
  name: string;
  type: string | null;
  timestamp: number;
}

// Type definition for recent accounts list (no organization nesting)
type RecentAccountsList = RecentAccount[];

// Type definition for selected account (just the accountId string)
type SelectedAccount = string;

/**
 * Hook for managing account selection with organization-scoping
 *
 * Features:
 * - Selects accounts and persists them in URL parameters and localStorage
 * - Tracks recently selected accounts per organization
 * - Synchronizes account selection between URL and recent accounts
 * - Falls back to previously selected account when URL has no accountId
 */
export function useAccountSelection() {
  const [searchParams, _setSearchParams] = useSearchParams();
  const params = useParams<{ accountId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Manage selected account in localStorage (organization-scoped automatically)
  const [selectedAccount, setSelectedAccount] =
    useTrackedLocalStorage<SelectedAccount>(SELECTED_ACCOUNTS_KEY, "");

  // Get account ID from URL path param first, then query param, then fallback to localStorage
  const accountIdFromParams = params.accountId;
  const accountIdFromQuery = searchParams.get("accountId") || undefined;
  const accountIdFromUrl = accountIdFromParams || accountIdFromQuery;
  const storedAccountId = selectedAccount || undefined;
  const selectedAccountId = accountIdFromUrl || storedAccountId;

  // Manage recent accounts in localStorage (organization-scoped automatically)
  const [recentAccounts, setRecentAccounts] =
    useTrackedLocalStorage<RecentAccountsList>(RECENT_ACCOUNTS_KEY, []);

  // Sort recent accounts by timestamp (most recent first)
  const sortedRecentAccounts = useMemo(() => {
    return [...recentAccounts].sort((a, b) => b.timestamp - a.timestamp);
  }, [recentAccounts]);

  // Find the selected account in recent accounts list
  const initialAccount = useMemo(() => {
    if (!selectedAccountId || recentAccounts.length === 0) return undefined;
    return recentAccounts.find((account) => account.id === selectedAccountId);
  }, [selectedAccountId, recentAccounts]);

  // Handle account selection
  const handleAccountChange = useCallback(
    (value: string, account: Account | RecentAccount | null) => {
      if (value) {
        // If we're on an accounts page, update the URL using the path format
        const isAccountPage =
          location.pathname.includes("/accounts/") &&
          (location.pathname.includes("/explorer") ||
            location.pathname.includes("/milestone-analyses"));

        if (location.pathname === "/accounts/explorer" || isAccountPage) {
          // Determine if we should preserve the current page type (milestone-analyses or explorer)
          const isMilestoneAnalysesPage = location.pathname.includes(
            "/milestone-analyses",
          );
          const targetPath = isMilestoneAnalysesPage
            ? `/accounts/${value}/milestone-analyses`
            : `/accounts/${value}/explorer`;

          navigate(targetPath, { replace: true });
        } else if (location.pathname.includes("/accounts/")) {
          // For any other accounts page, navigate to the explorer with the selected account
          navigate(`/accounts/${value}/explorer`, { replace: true });
        }
        // Don't add accountId as a query parameter to any page

        // Update localStorage for persistence across page loads
        setSelectedAccount(value);

        // Add to recent accounts if account object is provided
        if (account) {
          // Extract minimal fields and add with current timestamp (works for both full and recent objects)
          setRecentAccounts((prev) => {
            const currentList = prev || [];
            const filtered = currentList.filter((a) => a.id !== account.id);
            return [
              {
                id: account.id,
                name: account.name,
                type: account.type,
                timestamp: Date.now(),
              },
              ...filtered,
            ].slice(0, MAX_RECENT_ACCOUNTS);
          });
        }
      } else {
        // If we're on a path with an account ID, navigate back to the base explorer
        if (
          location.pathname.includes("/accounts/") &&
          (location.pathname.includes("/explorer") ||
            location.pathname.includes("/milestone-analyses")) &&
          params.accountId
        ) {
          navigate("/accounts/explorer", { replace: true });
        }
        // Don't manage query parameters at all

        // Remove from localStorage
        setSelectedAccount("");
      }
    },
    [
      setRecentAccounts,
      setSelectedAccount,
      location.pathname,
      navigate,
      params.accountId,
    ],
  );

  // Initialize URL from localStorage if needed
  useEffect(() => {
    // Only update URL for specific account explorer pages, never use query params
    if (!accountIdFromUrl && storedAccountId) {
      // Check if we're on a base explorer or milestone-analyses page
      const isMilestoneAnalysesPath = location.pathname.includes(
        "/milestone-analyses",
      );

      if (location.pathname === "/accounts/explorer") {
        // For explorer pages, use the new URL pattern
        navigate(`/accounts/${storedAccountId}/explorer`, { replace: true });
      } else if (isMilestoneAnalysesPath) {
        // For milestone analysis pages, use the appropriate URL pattern
        navigate(`/accounts/${storedAccountId}/milestone-analyses`, {
          replace: true,
        });
      }
      // Do not add accountId query param to any pages
    }
  }, [accountIdFromUrl, storedAccountId, location.pathname, navigate]);

  return {
    selectedAccountId,
    initialAccount,
    handleAccountChange,
    recentAccounts: sortedRecentAccounts,
    organizationId: undefined, // This is now handled internally by useTrackedLocalStorage
  };
}
